import { useId, useState } from 'react';
import { Link2, Plus, Search } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
  EmptyState,
  EntityText,
  HoldToConfirm,
  SearchSelect,
  Skeleton,
} from '@/components/ui';
import { availableObjects, validateRelated, type RelatedObject, type RelationState } from './relationData';
export function RelatedObjects({
  objects,
  candidates,
  onLink,
  onUnlink,
  onOpen,
  readOnly = false,
  state = 'ready',
  onRetry,
}: {
  objects: readonly RelatedObject[];
  candidates: readonly RelatedObject[];
  onLink?: (id: string) => void | Promise<void>;
  onUnlink?: (id: string) => void | Promise<void>;
  onOpen?: (id: string) => void;
  readOnly?: boolean;
  state?: RelationState;
  onRetry?: () => void;
}) {
  const id = useId(),
    [open, setOpen] = useState(false),
    [selected, setSelected] = useState<string | null>(null),
    [pending, setPending] = useState(false),
    [error, setError] = useState('');
  const invalid = validateRelated(objects) ?? validateRelated(candidates);
  const available = availableObjects(
    candidates,
    objects.map((item) => item.id),
  );
  async function action(run: () => void | Promise<void>, close = false) {
    setPending(true);
    setError('');
    try {
      await run();
      if (close) {
        setOpen(false);
        setSelected(null);
      }
    } catch {
      setError('Связь не сохранена. Попробуй ещё раз.');
    } finally {
      setPending(false);
    }
  }
  return (
    <section data-slot="related-objects" aria-labelledby={id} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 id={id} className="type-heading">
          Связанные <span className="text-fg-subtle">· {objects.length}</span>
        </h3>
        {!readOnly && onLink && (
          <Dialog
            open={open}
            onOpenChange={(next) => {
              if (!pending) {
                setOpen(next);
                setError('');
                setSelected(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" disabled={state !== 'ready' || !!invalid}>
                <Plus className="size-4" />
                Связать объект
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader
                title="Связать объект"
                description="Поиск по названию, типу и описанию. Уже связанные объекты скрыты."
              />
              <div className="space-y-4 px-6 pb-6">
                <SearchSelect
                  label="Объект"
                  value={selected}
                  onChange={setSelected}
                  options={available.map((item) => ({
                    value: item.id,
                    label: item.label,
                    description: item.kind + ' · ' + (item.description ?? ''),
                  }))}
                  disabled={pending}
                  placeholder="Найти объект"
                />
                {!available.length && (
                  <p className="text-sm text-fg-muted">Доступных объектов для связи нет.</p>
                )}
                {error && (
                  <p role="alert" className="text-sm text-danger-strong">
                    {error}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" disabled={pending} onClick={() => setOpen(false)}>
                    Отмена
                  </Button>
                  <Button
                    disabled={!selected || pending}
                    onClick={() => {
                      if (selected) void action(() => onLink(selected), true);
                    }}
                  >
                    {pending ? 'Сохранение…' : 'Связать'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {state === 'loading' ? (
        <div aria-label="Загрузка связанных объектов" className="space-y-3">
          {[0, 1, 2].map((n) => (
            <div key={n} className="flex items-center gap-3 rounded-md border border-line p-3">
              <Skeleton className="size-8 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : state === 'error' || invalid ? (
        <EmptyState
          title="Связи недоступны"
          description={invalid ?? 'Не удалось получить связанные объекты.'}
          icon={<Link2 />}
          action={onRetry ? { label: 'Повторить', onClick: onRetry } : undefined}
        />
      ) : !objects.length ? (
        <EmptyState
          title="Пока нет связей"
          description="Свяжи объект с заявкой, ресурсом или человеком."
          icon={<Link2 />}
        />
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {objects.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-3 p-3">
              <span className="grid size-8 shrink-0 place-items-center rounded-md bg-muted text-fg-subtle">
                <Link2 className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {onOpen ? (
                    <EntityText name={item.label} onClick={() => onOpen(item.id)} />
                  ) : (
                    <span className="text-sm font-medium">{item.label}</span>
                  )}
                  <Badge>{item.kind}</Badge>
                </div>
                {item.description && (
                  <p className="mt-1 text-xs break-words text-fg-muted">{item.description}</p>
                )}
              </div>
              {!readOnly && onUnlink && (
                <HoldToConfirm
                  size="sm"
                  disabled={pending}
                  onConfirm={() => void action(() => onUnlink(item.id))}
                >
                  Отвязать {item.label}
                </HoldToConfirm>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && !open && (
        <p role="alert" className="text-sm text-danger-strong">
          {error}
        </p>
      )}
      {!readOnly && (
        <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
          <Search className="size-3.5" />
          Поиск и права на связь предоставляет сервис.
        </p>
      )}
    </section>
  );
}
